import { liquidGlassPillShadowSx, liquidGlassSurfaceFillSx } from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC, type ReactNode, useEffect, useRef } from 'react';

export type SegmentedChipItem = {
  id: string;
  label: string;
  icon?: ReactNode;
};

type SegmentedChipBarProps = {
  items: SegmentedChipItem[];
  activeId: string;
  onChange: (id: string) => void;
  preserveFocusOnClick?: boolean;
};

type SegmentedChipProps = {
  item: SegmentedChipItem;
  selected: boolean;
  onSelect: () => void;
  preserveFocusOnClick: boolean;
  itemElRef: (el: HTMLButtonElement | null) => void;
};

const SegmentedChip: FC<SegmentedChipProps> = ({ item, selected, onSelect, preserveFocusOnClick, itemElRef }) => {
  return (
    <Box
      component="button"
      type="button"
      data-selected={selected}
      data-testid="template-category-chip"
      ref={itemElRef}
      onMouseDown={preserveFocusOnClick ? event => event.preventDefault() : undefined}
      onClick={() => {
        hapticFeedback.impactOccurred.ifAvailable('light');
        onSelect();
      }}
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        alignItems: 'center',
        gap: '6px',
        height: 32,
        px: '14px',
        m: 0,
        border: 'none',
        borderRadius: '999px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        bgcolor: selected ? 'color-mix(in srgb, var(--tg-theme-text-color) 12%, transparent)' : 'transparent',
        appearance: 'none',
        WebkitAppearance: 'none',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        transition: 'background-color 0.2s ease',
        '&:focus-visible': { outline: '2px solid var(--tg-theme-link-color)', outlineOffset: 2 },
      }}
    >
      {item.icon}
      <Text
        style={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          color: selected ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
        }}
      >
        {item.label}
      </Text>
    </Box>
  );
};

export const SegmentedChipBar: FC<SegmentedChipBarProps> = ({ items, activeId, onChange, preserveFocusOnClick = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemElsRef = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const container = containerRef.current;
    const item = itemElsRef.current[activeId];
    if (!container || !item) return;
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const delta = itemRect.left - containerRect.left;
    if (Math.abs(delta) < 1) return;
    container.scrollLeft += delta;
  }, [activeId, items]);

  return (
    <Box sx={{ borderRadius: '999px', overflow: 'visible', ...liquidGlassPillShadowSx }}>
      <Box
        ref={containerRef}
        data-testid="template-category-chip-scroll"
        sx={{
         display: 'flex',
           gap: '2px',
           p: '3px',
           pointerEvents: 'auto',
           overflowX: 'auto',
           scrollbarWidth: 'none',
          borderRadius: '999px',
          ...liquidGlassSurfaceFillSx,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map(item => (
          <SegmentedChip
            key={item.id}
            item={item}
            selected={item.id === activeId}
            onSelect={() => onChange(item.id)}
            preserveFocusOnClick={preserveFocusOnClick}
            itemElRef={el => {
              itemElsRef.current[item.id] = el;
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
